/**
 * 소스(매입 원가 근거) 옵션 — value/label/스냅샷의 **정본**.
 *
 * 왜 별도 모듈인가:
 *   소스를 고르는 화면이 둘이다 — 서버 구성 편집기와 딜 상세의 구성품 행.
 *   그런데 선택 라벨은 화면 문구가 아니라 `source_label` 로 **저장되는 값**이다.
 *   두 화면이 각자 문자열을 조립하면 같은 재고를 골라도 저장된 라벨이 갈라지고,
 *   나중에 "이 원가가 어디서 왔나"를 라벨로 되짚을 수 없게 된다.
 *
 * select value 형식은 `{source_type}:{id}` 하나다.
 *   재고 → `stock:{inventory_stock.id}`
 *   RFQ  → `deal_rfq:{rfq.id}` / `rfq_history:{rfq.id}`
 * 둘 다 숫자 id 라 그대로 `deal_item.source_ref_id`(BIGINT) 로 저장된다.
 *
 * ⚠ 예전에는 재고 후보에 id 가 실려 오지 않아 `stock:{컨디션}|{창고}` 라는
 *   문자열 키를 만들어 썼고, 그걸 BIGINT 에 넣으려다 NaN 으로 저장이 500 났다.
 *   그 키는 option_part 도 포함하지 않아 저장값만으로는 어느 파트인지도 알 수
 *   없었다. 이제 백엔드가 `stock_id` 를 실어 준다.
 */

import { escapeHtml } from './cells';
import type { StockCandidate, RfqCandidate } from '../api/server-config-api';

/**
 * RFQ 날짜 표기 — 기획 포맷 `YYYY/MM/DD`.
 * 날짜는 값을 고르는 근거다. 같은 매입처·컨디션이라도 언제 받은 가격인지에 따라
 * 쓸지 말지가 갈린다. 파싱 실패 시 빈 문자열이라 라벨에서 자연히 빠진다.
 */
function ymd(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}`
       + `/${String(d.getDate()).padStart(2, '0')}`;
}

/** 한 딜에서 조회해 둔 소스 후보 묶음 (GET /server-config/sources 응답 + 로딩 상태). */
export interface SourcePool {
  loading: boolean;
  stock:   readonly StockCandidate[];
  rfq:     readonly RfqCandidate[];
}

export interface SourceOption { value: string; label: string }

/** 선택 결과 스냅샷 — 그대로 저장 필드로 나간다. */
export interface SourceSnapshot {
  sourceType:   'stock' | 'deal_rfq' | 'rfq_history' | null;
  /** 재고=inventory_stock.id / RFQ=rfq.id. 그대로 source_ref_id 로 저장. */
  sourceRefId:  string | null;
  sourceLabel:  string | null;
  costAmount:   number | null;
  costCurrency: string | null;
}

const won = (n: number): string => Number(n).toLocaleString('ko-KR');

const money = (amt: number | null, cur: string | null): string =>
  // 원가 없음을 0 으로 치환하지 않는다 — '확인 필요' 가 근거 없음의 표시다.
  amt == null ? '확인 필요' : `${cur === 'USD' ? '$' : '₩'}${won(amt)}`;

/**
 * 재고 상태(quality_note) 라벨 — 마이그 170.
 *
 * 같은 PN·컨디션·창고인데 상태만 다른 행이 있다(171: 755258-B21 · REF 가
 * P440ar 정상 1대 / 녹·불량 6대). 이 조각이 없으면 두 옵션이
 * '보유재고 · REF · N개' 로 똑같아져 어느 쪽을 고르는지 알 수 없다.
 * 그리고 이 라벨은 source_label 로 **저장되는 값**이라(위 모듈 주석) 나중에
 * '이 원가가 불량품에서 왔나'를 되짚는 유일한 근거가 된다.
 *
 * ★ 모르는 값은 코드를 그대로 노출한다. 상태 어휘는 관측될 때마다 늘어나는데
 *   매핑에 없다고 빈 문자열을 돌려주면 새 상태가 조용히 사라진다.
 */
const QUALITY_LABEL: Record<string, string> = {
  DEFECT:       '불량',
  NO_MAINBOARD: '메인보드 결품',
};
const quality = (q: string | null | undefined): string => {
  const key = (q ?? '').trim();
  if (!key) return '';
  return ` · ${QUALITY_LABEL[key] ?? key}`;
};

/** 한 파트의 소스 옵션 목록. */
export function sourceOptions(pool: SourcePool, part: string): SourceOption[] {
  const stock = pool.stock
    .filter((s) => s.option_part === part)
    .map((s) => ({
      value: `stock:${s.stock_id}`,
      label: `보유재고 · ${s.condition}${quality(s.quality_note)}`
           + ` · ${s.qty_available}개 · ${money(s.cost_amount, s.cost_currency)}`,
    }));

  const rfq = pool.rfq
    .filter((s) => s.option_part === part)
    .map((s) => ({
      value: `${s.source_type}:${s.rfq_id}`,
      // 출처 표기는 두 가지뿐이다 — `보유재고` / `RFQ`.
      //
      // 예전엔 이 딜의 RFQ 는 'RFQ', 다른 딜에서 받은 건 '이력' 으로 갈랐다.
      // 고르는 사람 입장에선 둘 다 "매입처가 준 가격"이라 구분이 의미가 없고,
      // '이력' 이라는 말이 오히려 지난 기록처럼 읽혀 쓸 수 없는 값으로 보였다.
      // 어느 딜에서 왔는지는 값을 고르는 근거가 아니다 — 매입처·컨디션·가격이다.
      // 날짜는 값을 고르는 근거다 — 같은 매입처·컨디션이라도 언제 받은 가격인지에
      // 따라 쓸지 말지가 갈린다. 52cff5d 에서 표기를 통일하며 빠졌던 것을 되살린다.
      label: ['RFQ', ymd(s.rfq_date),
              s.supplier_name + (s.is_alternative ? ' (대체)' : ''),
              s.condition ?? '-',
              money(s.price_amount, s.price_currency)]
             .filter(Boolean).join(' · '),
    }));

  return [...stock, ...rfq];
}

/** 저장용 라벨 조회 — 옵션 목록과 같은 문자열을 돌려준다. */
export function sourceLabelFor(pool: SourcePool, part: string, value: string): string | null {
  return sourceOptions(pool, part).find((o) => o.value === value)?.label ?? null;
}

/**
 * select value → 저장 스냅샷.
 *
 * 원가는 **고른 시점의 값을 복사**한다. 재고가 팔리거나 RFQ 가 갱신돼도 이미
 * 계산한 마진이 소급해서 바뀌면 안 되기 때문이다.
 */
export function sourceSnapshot(pool: SourcePool, part: string, value: string): SourceSnapshot {
  if (value === '') {
    return { sourceType: null, sourceRefId: null, sourceLabel: null, costAmount: null, costCurrency: null };
  }
  const sep  = value.indexOf(':');
  const type = value.slice(0, sep) as SourceSnapshot['sourceType'];
  const ref  = value.slice(sep + 1);

  if (type === 'stock') {
    const hit = pool.stock.find((s) => s.stock_id === ref);
    return {
      sourceType:   'stock',
      sourceRefId:  ref || null,
      sourceLabel:  sourceLabelFor(pool, part, value),
      costAmount:   hit?.cost_amount   ?? null,
      costCurrency: hit?.cost_currency ?? null,
    };
  }

  const hit = pool.rfq.find((s) => String(s.rfq_id) === ref);
  return {
    sourceType:   type,
    sourceRefId:  ref || null,
    sourceLabel:  sourceLabelFor(pool, part, value),
    costAmount:   hit?.price_amount   ?? null,
    costCurrency: hit?.price_currency ?? null,
  };
}

/** 저장된 스냅샷 → select 의 selected value. 종류와 무관하게 id 하나다. */
export function selectedSourceValue(
  sourceType: string | null | undefined,
  refId:      string | null | undefined,
): string {
  if (!sourceType || !refId) return '';
  return `${sourceType}:${refId}`;
}

/** 소스 select 마크업. `cls` 로 화면별 스타일을 받는다. */
export function sourceSelectHtml(opts: {
  pool:     SourcePool;
  part:     string;
  selected: string;
  attrs:    string;
  cls:      string;
  disabled?: boolean;
}): string {
  if (!opts.part) {
    return `<select class="${opts.cls}" disabled><option>품목 먼저 선택</option></select>`;
  }
  const list = sourceOptions(opts.pool, opts.part);
  return `
    <select class="${opts.cls}" ${opts.attrs} ${opts.disabled ? 'disabled' : ''}>
      <option value="">${list.length === 0
        ? (opts.pool.loading ? '불러오는 중…' : '후보 없음')
        : '소스 선택'}</option>
      ${list.map((o) => `<option value="${escapeHtml(o.value)}" ${
        opts.selected === o.value ? 'selected' : ''
      }>${escapeHtml(o.label)}</option>`).join('')}
    </select>
  `;
}
